import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  formatGscUpdatedAtUtc,
  parseGscDateRangeFromUrl,
  resolveGscDateRange,
  syncGscDateRangeSearchParams,
} from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import {
  GSC_DATE_RANGE_LABELS,
  GSC_DATE_RANGE_OPTIONS,
  GSC_DEFAULT_DATE_RANGE,
  type GscDateRangeOption,
} from "@/types/gsc";

export type GscDateRangeControlsVariant = "overview" | "detail" | "compare";

interface GscDateRangeControlsProps {
  variant: GscDateRangeControlsVariant;
  /** TanStack Query `dataUpdatedAt` or similar — shown in the freshness badge. */
  dataUpdatedAt?: number;
  /** Toolbar mode — no outer card chrome (used in `GscLayout`). */
  embedded?: boolean;
  className?: string;
}

const getCustomDraftDefaults = (
  customFrom?: string,
  customTo?: string,
): { from: string; to: string } => {
  if (customFrom && customTo) {
    return { from: customFrom, to: customTo };
  }

  const { dateFrom, dateTo } = resolveGscDateRange(GSC_DEFAULT_DATE_RANGE);
  return { from: dateFrom, to: dateTo };
};

export const GscDateRangeControls = ({
  variant,
  dataUpdatedAt,
  embedded = false,
  className,
}: GscDateRangeControlsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customOpen, setCustomOpen] = useState(false);
  const [customDraftFrom, setCustomDraftFrom] = useState("");
  const [customDraftTo, setCustomDraftTo] = useState("");

  const { dateRange, customFrom, customTo } =
    parseGscDateRangeFromUrl(searchParams);

  useEffect(() => {
    const normalized = syncGscDateRangeSearchParams(searchParams);
    if (!normalized) return;

    setSearchParams(normalized, { replace: true });
  }, [searchParams, setSearchParams]);

  const updateSearchParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        mutate(prev);
        return prev;
      });
    },
    [setSearchParams],
  );

  const resetPagination = useCallback(
    (params: URLSearchParams) => {
      if (variant === "overview") {
        params.delete("ovPage");
        return;
      }
      params.delete("page");
    },
    [variant],
  );

  const openCustomPopover = useCallback(() => {
    const draft = getCustomDraftDefaults(
      dateRange === "custom" ? customFrom : undefined,
      dateRange === "custom" ? customTo : undefined,
    );
    setCustomDraftFrom(draft.from);
    setCustomDraftTo(draft.to);
    setCustomOpen(true);
  }, [customFrom, customTo, dateRange]);

  const handlePresetSelect = useCallback(
    (preset: GscDateRangeOption) => {
      if (preset === "custom") {
        openCustomPopover();
        return;
      }

      setCustomOpen(false);
      updateSearchParams((params) => {
        params.set("dateRange", preset);
        params.delete("from");
        params.delete("to");
        resetPagination(params);
      });
    },
    [openCustomPopover, resetPagination, updateSearchParams],
  );

  const handleCustomApply = useCallback(() => {
    if (!customDraftFrom || !customDraftTo) return;

    setCustomOpen(false);
    updateSearchParams((params) => {
      params.set("dateRange", "custom");
      params.set("from", customDraftFrom);
      params.set("to", customDraftTo);
      resetPagination(params);
    });
  }, [customDraftFrom, customDraftTo, resetPagination, updateSearchParams]);

  const handleCustomOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openCustomPopover();
        return;
      }
      setCustomOpen(false);
    },
    [openCustomPopover],
  );

  const updatedLabel =
    dataUpdatedAt != null ? formatGscUpdatedAtUtc(dataUpdatedAt) : null;

  const canApplyCustom = Boolean(customDraftFrom && customDraftTo);

  return (
    <div
      className={cn(
        embedded
          ? "bg-transparent p-0"
          : "rounded-lg border border-border bg-card p-3 sm:p-4",
        className,
      )}
    >
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
          <div
            role="radiogroup"
            aria-label="GSC date range"
            className="inline-flex w-fit max-w-full flex-wrap gap-0.5 rounded-lg border border-border bg-muted/50 p-1"
          >
            {GSC_DATE_RANGE_OPTIONS.map((preset) => {
              const isCustom = preset === "custom";
              const isActive = dateRange === preset;

              if (isCustom) {
                return (
                  <Popover
                    key={preset}
                    open={customOpen}
                    onOpenChange={handleCustomOpenChange}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={openCustomPopover}
                        className={cn(
                          "cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {GSC_DATE_RANGE_LABELS.custom}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto border-border bg-popover p-4 shadow-none"
                    >
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="gsc-custom-from"
                            className="cursor-pointer text-xs text-muted-foreground"
                          >
                            From
                          </Label>
                          <Input
                            id="gsc-custom-from"
                            type="date"
                            value={customDraftFrom}
                            onChange={(event) =>
                              setCustomDraftFrom(event.target.value)
                            }
                            className="h-9 tabular-nums"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="gsc-custom-to"
                            className="cursor-pointer text-xs text-muted-foreground"
                          >
                            To
                          </Label>
                          <Input
                            id="gsc-custom-to"
                            type="date"
                            value={customDraftTo}
                            onChange={(event) =>
                              setCustomDraftTo(event.target.value)
                            }
                            className="h-9 tabular-nums"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={!canApplyCustom}
                          onClick={handleCustomApply}
                        >
                          Apply
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }

              return (
                <button
                  key={preset}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => handlePresetSelect(preset)}
                  className={cn(
                    "cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                    isActive
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {GSC_DATE_RANGE_LABELS[preset]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="signal-card-warning rounded-full border px-2.5 py-1 font-normal"
          >
            <AlertCircle className="size-3.5 signal-text-warning" />
            <span className="signal-text-warning">
              Last 3 days may recalculate (GSC window)
            </span>
          </Badge>

          {updatedLabel ? (
            <Badge
              variant="outline"
              className="signal-card-positive rounded-full border px-2.5 py-1 font-normal"
            >
              <CheckCircle2 className="size-3.5 signal-text-positive" />
              <span className="signal-text-positive">
                Updated{" "}
                <span className="font-semibold tabular-nums">
                  {updatedLabel}
                </span>
              </span>
            </Badge>
          ) : null}
        </div>
      </div>
    </div>
  );
};
